import React from 'react';
import { Key, AlertCircle } from 'lucide-react';
import { AI_MODELS } from '../utils/aiModels';

interface AISettingsProps {
  useAI: boolean;
  setUseAI: (useAI: boolean) => void;
  apiKey: string;
  setApiKey: (key: string) => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
}

export function AISettings({ 
  useAI, 
  setUseAI, 
  apiKey, 
  setApiKey,
  selectedModel,
  setSelectedModel
}: AISettingsProps) {
  const groupedModels = AI_MODELS.reduce((acc, model) => {
    const provider = model.provider.charAt(0).toUpperCase() + model.provider.slice(1);
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(model);
    return acc;
  }, {} as Record<string, typeof AI_MODELS>);

  return (
    <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="useAI"
          checked={useAI}
          onChange={(e) => setUseAI(e.target.checked)}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="useAI" className="text-sm text-gray-700">
          Use AI-powered generation
        </label>
      </div>

      {useAI && (
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select AI Model
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {Object.entries(groupedModels).map(([provider, models]) => (
                <optgroup key={provider} label={provider}>
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} - {model.description}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                API Key
              </span>
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your API key"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              {selectedModel.includes('gpt') ? 'OpenAI API key required' :
               selectedModel.includes('claude') ? 'Anthropic API key required' :
               'Google AI API key required'}
            </p>
          </div>

          {!apiKey && (
            <div className="flex items-start gap-2 text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <p>
                Please provide an API key for the selected model. Your key is stored locally and never sent to our servers.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}